import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/Layout';

interface Meta {
  id: number;
  nome: string;
  tipo: string;
  valor: string;
  unidade: string;
  peso: number;
}

export default function ObjetivosPage() {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [metas, setMetas] = useState<Meta[]>([
    {
      id: 1,
      nome: 'Faturamento Mensal',
      tipo: 'vendas',
      valor: '100000',
      unidade: 'R$',
      peso: 40
    },
    {
      id: 2,
      nome: 'Novos Clientes',
      tipo: 'aquisicao',
      valor: '50',
      unidade: 'clientes',
      peso: 30
    },
    {
      id: 3,
      nome: 'Taxa de Conversão',
      tipo: 'performance',
      valor: '25',
      unidade: '%',
      peso: 30
    }
  ]);

  const [novaMeta, setNovaMeta] = useState({
    nome: '',
    tipo: '',
    valor: '',
    unidade: '',
    peso: 0
  });

  const handleAddMeta = () => {
    if (novaMeta.nome && novaMeta.tipo && novaMeta.valor) {
      setMetas([...metas, { ...novaMeta, id: Date.now() }]);
      setNovaMeta({ nome: '', tipo: '', valor: '', unidade: '', peso: 0 });
      setShowAddModal(false);
    }
  };

  const handleRemoveMeta = (id: number) => {
    setMetas(metas.filter(meta => meta.id !== id));
  };

  const getTipoColor = (tipo: string) => {
    const colors = {
      'vendas': 'bg-blue-100 text-blue-800',
      'aquisicao': 'bg-emerald-100 text-emerald-800',
      'performance': 'bg-orange-100 text-orange-800',
      'engajamento': 'bg-purple-100 text-purple-800'
    };
    return colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTipoLabel = (tipo: string) => {
    const labels = {
      'vendas': 'Vendas',
      'aquisicao': 'Aquisição',
      'performance': 'Performance',
      'engajamento': 'Engajamento'
    };
    return labels[tipo as keyof typeof labels] || tipo;
  };

  const handleProximo = () => {
    navigate('/admin/incentivo/campanhas/acoes');
  };

  const handleVoltar = () => {
    navigate('/admin/incentivo/campanhas/dados-basicos');
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Objetivos e Metas</h1>
            <p className="text-gray-600 mt-1">Defina os objetivos e metas da campanha de incentivo</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            <span>Adicionar Meta</span>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line"></i>
                </div>
              </div>
              <span className="text-gray-600">Dados Básicos</span>
            </div>
            <div className="flex-1 h-1 bg-blue-600 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                2
              </div>
              <span className="font-semibold text-gray-900">Objetivos</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                3
              </div>
              <span className="text-gray-600">Ações</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                4
              </div>
              <span className="text-gray-600">Regras</span>
            </div>
            <div className="flex-1 h-1 bg-gray-200 mx-4"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center font-semibold">
                5
              </div>
              <span className="text-gray-600">Premiação</span>
            </div>
          </div>
        </div>

        {/* Metas List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Metas Configuradas</h3>
          
          {metas.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-target-line text-gray-400 text-2xl"></i>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma meta configurada</h3>
              <p className="text-gray-600 mb-4">Adicione metas para definir os objetivos da campanha</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Adicionar Primeira Meta
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {metas.map((meta) => (
                <div key={meta.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-base font-semibold text-gray-900">{meta.nome}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(meta.tipo)}`}>
                        {getTipoLabel(meta.tipo)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveMeta(meta.id)}
                      className="text-red-600 hover:text-red-800 cursor-pointer"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-delete-bin-line"></i>
                      </div>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Valor Meta</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {meta.unidade === 'R$' ? `R$ ${Number(meta.valor).toLocaleString('pt-BR')}` : `${meta.valor} ${meta.unidade}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Peso</p>
                      <p className="text-sm font-semibold text-gray-900">{meta.peso}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Progresso</p>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full" style={{ width: '0%' }}></div>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">0%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Total Weight */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Peso Total das Metas</span>
                  <span className={`text-lg font-bold ${metas.reduce((sum, m) => sum + m.peso, 0) === 100 ? 'text-emerald-600' : 'text-orange-600'}`}>
                    {metas.reduce((sum, m) => sum + m.peso, 0)}%
                  </span>
                </div>
                {metas.reduce((sum, m) => sum + m.peso, 0) !== 100 && (
                  <p className="text-xs text-orange-600 mt-1">O peso total deve somar 100%</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Add Meta Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Adicionar Meta</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line text-xl"></i>
                  </div>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Meta</label>
                  <input
                    type="text"
                    value={novaMeta.nome}
                    onChange={(e) => setNovaMeta({ ...novaMeta, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Ex: Faturamento Mensal"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                  <select
                    value={novaMeta.tipo}
                    onChange={(e) => setNovaMeta({ ...novaMeta, tipo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="vendas">Vendas</option>
                    <option value="aquisicao">Aquisição</option>
                    <option value="performance">Performance</option>
                    <option value="engajamento">Engajamento</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
                    <input
                      type="number"
                      value={novaMeta.valor}
                      onChange={(e) => setNovaMeta({ ...novaMeta, valor: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unidade</label>
                    <select
                      value={novaMeta.unidade}
                      onChange={(e) => setNovaMeta({ ...novaMeta, unidade: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
                    >
                      <option value="">Selecione</option>
                      <option value="R$">R$</option>
                      <option value="%">%</option>
                      <option value="clientes">Clientes</option>
                      <option value="vendas">Vendas</option>
                      <option value="pontos">Pontos</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Peso (%)</label>
                  <input
                    type="number"
                    value={novaMeta.peso}
                    onChange={(e) => setNovaMeta({ ...novaMeta, peso: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAddMeta}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <button 
            onClick={handleVoltar}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            <span>Voltar: Dados Básicos</span>
          </button>
          <button 
            onClick={handleProximo}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2"
          >
            <span>Próximo: Ações e Pontos</span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </button>
        </div>
      </div>
    </Layout>
  );
}
